import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import api, { cacheGet, cacheSet, downloadApiBlob, listFrom } from '../api';

function fmtSize(n) {
  const b = Number(n) || 0;
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(2)} MB`;
}

export default function Exports() {
  const [items, setItems] = useState(() => cacheGet('exports', []));
  const [pending, setPending] = useState(0);
  const [loading, setLoading] = useState(false);

  const reload = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get('/exports'),
      api.get('/logs/export/pending').catch(() => ({ data: { pending_export: 0 } })),
    ])
      .then(([expRes, pendRes]) => {
        const list = listFrom(expRes.data, 'exports');
        setItems(list);
        cacheSet('exports', list);
        setPending(Number(pendRes.data?.pending_export) || 0);
      })
      .catch(() => {
        toast.error('Не удалось загрузить выгрузки');
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  function exportNew() {
    if (!pending) {
      toast.error('Нет новых логов для выгрузки');
      return;
    }
    try {
      downloadApiBlob('/logs/export/all-zip', 'apex_logs_export.zip');
      toast.success(`Выгрузка ${pending} лог(ов) — сохранено в историю, пароль из Настроек`);
      setTimeout(() => reload(), 2500);
    } catch (err) {
      toast.error(err?.message || 'Не удалось выгрузить');
    }
  }

  function downloadBatch(id, filename) {
    if (!id) return;
    try {
      downloadApiBlob(`/exports/${id}/download`, filename || `export_${id}.zip`);
      toast.success('Скачивание…');
    } catch (err) {
      toast.error(err?.message || 'Не удалось скачать');
    }
  }

  async function removeBatch(id) {
    if (!id) return;
    if (!window.confirm('Удалить эту выгрузку с сервера?')) return;
    try {
      await api.delete(`/exports/${id}`);
      toast.success('Удалено');
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Не удалось удалить');
    }
  }

  return (
    <div>
      <div className="rm-page-head">
        <div className="rm-page-title">Выгрузки логов</div>
        <button type="button" className="rm-btn primary" onClick={exportNew} disabled={!pending}>
          Выгрузить новые{pending ? ` (${pending})` : ''}
        </button>
      </div>
      <div className="rm-help">
        <b>Помощь</b>
        «Выгрузить новые» собирает только ещё не выгруженные логи в ZIP с паролем из Настроек
        и сохраняет архив здесь — можно скачать снова в любой момент.
        Одиночный ZIP по логу (из Мои логи) — без пароля и в эту историю не попадает.
      </div>
      <div style={{ marginBottom: 12 }}>
        <span className="rm-badge">{pending} к выгрузке</span>
      </div>
      <div className={`rm-card rm-table-wrap${loading ? ' rm-loading' : ''}`}>
        <table className="rm-table">
          <thead>
            <tr>
              <th>Дата</th>
              <th>Файл</th>
              <th>Логов</th>
              <th>Размер</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {(items || []).map((x) => (
              <tr key={x.id}>
                <td>{x.exported_at || x.created_at || '—'}</td>
                <td>{x.filename || `export_${x.id}.zip`}</td>
                <td>{x.log_count ?? (x.log_ids?.length || '—')}</td>
                <td>{fmtSize(x.size_bytes)}</td>
                <td style={{ display: 'flex', gap: 8 }}>
                  <button
                    type="button"
                    className="rm-btn ghost"
                    onClick={() => downloadBatch(x.id, x.filename)}
                  >
                    Скачать
                  </button>
                  <button type="button" className="rm-btn ghost" onClick={() => removeBatch(x.id)}>
                    Удалить
                  </button>
                </td>
              </tr>
            ))}
            {!items.length && !loading && (
              <tr><td colSpan={5} className="rm-empty">Пока пусто — нажми «Выгрузить новые»</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
